import { Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  GripVertical,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useTradeCategories } from "@/hooks/use-config";
import { useFolderTemplatePaths, useFolderTemplates } from "@/hooks/use-folders";
import type { TaskTemplate, TaskTemplateDetail, TaskTemplateItem } from "@/hooks/use-tasks";
import {
  useCreateTaskTemplate,
  useCreateTaskTemplateItem,
  useDeleteTaskTemplate,
  useDeleteTaskTemplateItem,
  useTaskTemplate,
  useTaskTemplates,
  useUpdateTaskTemplate,
  useUpdateTaskTemplateItem,
} from "@/hooks/use-tasks";

const PROJECT_TYPES = [
  "Residential Renovation",
  "Mixed-Use Conversion",
  "Ground-Up Commercial",
  "Interior Fit-Out",
  "Infrastructure",
] as const;

// ── Create / Edit Template Dialog ──

function TemplateDialog({
  template,
  open,
  onOpenChange,
}: {
  template: TaskTemplate | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const createTemplate = useCreateTaskTemplate();
  const updateTemplate = useUpdateTaskTemplate();
  const { data: folderTemplates } = useFolderTemplates();
  const [name, setName] = useState(template?.name ?? "");
  const [description, setDescription] = useState(template?.description ?? "");
  const [projectType, setProjectType] = useState(template?.projectType ?? "any");
  const [folderTemplateId, setFolderTemplateId] = useState(template?.folderTemplateId ?? "none");
  const [status, setStatus] = useState(template?.status ?? "draft");

  const isEdit = template !== null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload = {
      name,
      description: description || null,
      projectType: projectType === "any" ? null : projectType || null,
      folderTemplateId: folderTemplateId === "none" ? null : folderTemplateId,
      status,
    };
    if (isEdit && template) {
      updateTemplate.mutate(
        { id: template.id, ...payload },
        { onSuccess: () => onOpenChange(false) },
      );
    } else {
      createTemplate.mutate(payload, { onSuccess: () => onOpenChange(false) });
    }
  }

  const isPending = isEdit ? updateTemplate.isPending : createTemplate.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Template" : "Create Template"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="tmpl-name">Name</Label>
            <Input id="tmpl-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tmpl-desc">Description</Label>
            <Textarea
              id="tmpl-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tmpl-type">Project Type</Label>
              <Select value={projectType} onValueChange={setProjectType}>
                <SelectTrigger id="tmpl-type">
                  <SelectValue placeholder="Any type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  {PROJECT_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tmpl-status">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger id="tmpl-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tmpl-folder">Folder Template</Label>
            <Select value={folderTemplateId} onValueChange={setFolderTemplateId}>
              <SelectTrigger id="tmpl-folder">
                <SelectValue placeholder="Select folder template..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {(folderTemplates ?? []).map((ft) => (
                  <SelectItem key={ft.id} value={ft.id}>
                    {ft.name ?? ft.id}
                    {ft.isDefault ? " (default)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Task items will use this template's folders for evidence filing.
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Saving..." : isEdit ? "Save Changes" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Add / Edit Template Item Dialog ──

function ItemDialog({
  templateId,
  folderTemplateId,
  item,
  open,
  onOpenChange,
}: {
  templateId: string;
  folderTemplateId: string | null;
  item: TaskTemplateItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const createItem = useCreateTaskTemplateItem(templateId);
  const updateItem = useUpdateTaskTemplateItem(templateId);
  const { data: trades } = useTradeCategories();
  const { data: folderPaths } = useFolderTemplatePaths(folderTemplateId ?? "");
  const [name, setName] = useState(item?.name ?? "");
  const [sequence, setSequence] = useState(String(item?.sequence ?? 10));
  const [offsetDays, setOffsetDays] = useState(String(item?.offsetDays ?? 0));
  const [tradeId, setTradeId] = useState(item?.tradeId ?? "");
  const [evidenceRequired, setEvidenceRequired] = useState(item?.evidenceRequired ?? false);
  const [defaultEvidenceFolderPath, setDefaultEvidenceFolderPath] = useState(
    item?.defaultEvidenceFolderPath ?? "",
  );

  const isEdit = item !== null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload = {
      name,
      sequence: Number(sequence),
      offsetDays: Number(offsetDays),
      tradeId: tradeId || null,
      evidenceRequired,
      defaultEvidenceFolderPath: defaultEvidenceFolderPath || null,
    };
    if (isEdit && item) {
      updateItem.mutate({ taskId: item.id, ...payload }, { onSuccess: () => onOpenChange(false) });
    } else {
      createItem.mutate(payload, { onSuccess: () => onOpenChange(false) });
    }
  }

  const isPending = isEdit ? updateItem.isPending : createItem.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Task Item" : "Add Task Item"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="item-name">Name</Label>
            <Input id="item-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="item-seq">Sequence</Label>
              <Input
                id="item-seq"
                type="number"
                value={sequence}
                onChange={(e) => setSequence(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="item-offset">Offset Days</Label>
              <Input
                id="item-offset"
                type="number"
                value={offsetDays}
                onChange={(e) => setOffsetDays(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="item-trade">Trade</Label>
            <Select
              value={tradeId || "none"}
              onValueChange={(v) => setTradeId(v === "none" ? "" : v)}
            >
              <SelectTrigger id="item-trade">
                <SelectValue placeholder="Select trade..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— No trade —</SelectItem>
                {(trades ?? []).map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                    {t.csiDivision ? ` (${t.csiDivision})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <div className="space-y-0.5">
              <Label htmlFor="item-evidence-required">Evidence Required</Label>
              <p className="text-xs text-muted-foreground">
                Task cannot be completed without an uploaded file.
              </p>
            </div>
            <Switch
              id="item-evidence-required"
              checked={evidenceRequired}
              onCheckedChange={setEvidenceRequired}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="item-evidence-folder">Default Evidence Folder</Label>
            {folderTemplateId ? (
              <Select
                value={defaultEvidenceFolderPath || "none"}
                onValueChange={(v) => setDefaultEvidenceFolderPath(v === "none" ? "" : v)}
              >
                <SelectTrigger id="item-evidence-folder">
                  <SelectValue placeholder="Select folder..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {(folderPaths ?? []).map((p) =>
                    p.fullPath ? (
                      <SelectItem key={p.id} value={p.fullPath}>
                        {p.fullPath}
                      </SelectItem>
                    ) : null,
                  )}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-xs text-muted-foreground">
                Link this task template to a folder template to enable evidence folder selection.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Saving..." : isEdit ? "Save Changes" : "Add Item"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Template Detail (expanded) ──

function TemplateDetail({ templateId }: { templateId: string }) {
  const { data, isLoading, isError, error, refetch } = useTaskTemplate(templateId);
  const { data: trades } = useTradeCategories();
  const deleteItem = useDeleteTaskTemplateItem(templateId);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<TaskTemplateItem | null>(null);

  if (isLoading) return <Skeleton className="h-32 w-full" />;
  if (isError) return <ErrorState message={error?.message} onRetry={() => refetch()} />;

  const detail = data as TaskTemplateDetail | undefined;
  const items = detail?.items ?? [];
  const tradeMap = new Map((trades ?? []).map((t) => [t.id, t.name]));

  return (
    <div className="space-y-3 pl-8 pb-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-muted-foreground">Task Items ({items.length})</h4>
        <Button variant="ghost" size="sm" onClick={() => setAddItemOpen(true)}>
          <Plus className="mr-1 h-3 w-3" />
          Add Item
        </Button>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No task items defined yet.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Name</TableHead>
              <TableHead className="w-24">Sequence</TableHead>
              <TableHead className="w-28">Offset Days</TableHead>
              <TableHead>Trade</TableHead>
              <TableHead className="w-20" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                </TableCell>
                <TableCell className="font-medium">{item.name}</TableCell>
                <TableCell>{item.sequence}</TableCell>
                <TableCell>{item.offsetDays}</TableCell>
                <TableCell>{item.tradeId ? (tradeMap.get(item.tradeId) ?? "--") : "--"}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setEditingItem(item)}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={() => deleteItem.mutate(item.id)}
                      disabled={deleteItem.isPending}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <ItemDialog
        templateId={templateId}
        folderTemplateId={detail?.folderTemplateId ?? null}
        item={null}
        open={addItemOpen}
        onOpenChange={setAddItemOpen}
      />
      {editingItem ? (
        <ItemDialog
          templateId={templateId}
          folderTemplateId={detail?.folderTemplateId ?? null}
          item={editingItem}
          open={true}
          onOpenChange={(o) => {
            if (!o) setEditingItem(null);
          }}
        />
      ) : null}
    </div>
  );
}

// ── Template Row ──

function TemplateRow({
  template,
  onEdit,
}: {
  template: TaskTemplate;
  onEdit: (t: TaskTemplate) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const deleteTemplate = useDeleteTaskTemplate();

  return (
    <>
      <TableRow className="cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <TableCell>
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </TableCell>
        <TableCell className="font-medium">{template.name}</TableCell>
        <TableCell className="text-muted-foreground max-w-xs truncate">
          {template.description ?? "--"}
        </TableCell>
        <TableCell>{template.projectType ?? "Any"}</TableCell>
        <TableCell>
          <Badge
            variant={
              template.status === "active"
                ? "success"
                : template.status === "archived"
                  ? "secondary"
                  : "default"
            }
          >
            {template.status}
          </Badge>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => onEdit(template)}
            >
              <Pencil className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive"
              onClick={() => deleteTemplate.mutate(template.id)}
              disabled={deleteTemplate.isPending}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {expanded ? (
        <TableRow>
          <TableCell colSpan={6} className="p-0 bg-muted/30">
            <TemplateDetail templateId={template.id} />
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

// ── Main Page ──

export function TaskTemplatesPage() {
  const { data, isLoading, isError, error, refetch } = useTaskTemplates();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TaskTemplate | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return <ErrorState message={error?.message} onRetry={() => refetch()} />;
  }

  const templates = data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Task Templates"
        description="Manage reusable task templates for projects"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild>
              <Link to="/admin/config">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Configuration
              </Link>
            </Button>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Template
            </Button>
          </div>
        }
      />

      {templates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <ClipboardList className="mb-4 h-12 w-12 text-muted-foreground/50" />
            <h3 className="text-lg font-semibold">No templates yet</h3>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              Create your first task template to standardize project task creation.
            </p>
            <Button className="mt-4" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Project Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((t) => (
                <TemplateRow key={t.id} template={t} onEdit={setEditingTemplate} />
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <TemplateDialog template={null} open={createOpen} onOpenChange={setCreateOpen} />
      {editingTemplate ? (
        <TemplateDialog
          template={editingTemplate}
          open={true}
          onOpenChange={(o) => {
            if (!o) setEditingTemplate(null);
          }}
        />
      ) : null}
    </div>
  );
}
